import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as actions from './actionCreators';
import { WebAuthnStatus } from './actionCreators';
import PageWebAuthnRegisterBody from './PageWebAuthnRegisterBody';
import { useAuth, useDebugRender } from '../hooks';
import { getLabelFromJspVariables } from '../common/JspUtil';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

const PageWebAuthnRegister = ({ onSetAsDefault, onDeviceRegistered }) => {
    useDebugRender('PageWebAuthnRegister');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { authenticatedUser, isAuthenticated } = useAuth();
    const webAuthnRegistration = useSelector(state => state.tfaReducer.tfaStatus.webAuthnRegistration);

    const [deviceName, setDeviceName] = useState('');
    const [startRegistration, setStartRegistration] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(constants.ROUTE_PAGE_LOGIN);
        }
        return () => {
            dispatch({ type: actions.TFA_CLEAR_ERROR });
        };
    }, []);

    useEffect(() => {
        if (webAuthnRegistration === WebAuthnStatus.SUCCESS && onDeviceRegistered) {
            onDeviceRegistered();
        }
    }, [webAuthnRegistration]);

    const onDeviceNameChange = (val) => {
        setDeviceName(val);
        if (fieldErrors.deviceName) setFieldErrors({});
    };

    const handleStartRegistration = () => {
        if (!deviceName || !deviceName.trim()) {
            setFieldErrors({ deviceName: 'Device name is required' });
            return;
        }
        setFieldErrors({});
        setStartRegistration(true);
        dispatch(actions.getWebAuthnStartRegistrationRequest(
            authenticatedUser.authenticatedApp,
            authenticatedUser.authenticatedUser,
            deviceName
        ));
    };

    const registerAnother = () => {
        setDeviceName('');
        setStartRegistration(false);
        setFieldErrors({});
        dispatch({ type: actions.TFA_UPDATE_STATUS, data: { webAuthnRegistration: WebAuthnStatus.IDLE } });
    };

    return (
        <Box>
            <PageWebAuthnRegisterBody
                deviceName={deviceName}
                onDeviceNameChange={onDeviceNameChange}
                fieldErrors={fieldErrors}
                startRegistration={startRegistration}
            />
            <Box sx={{ px: '1.125rem' }}>
                {!startRegistration &&
                    <Button variant="contained" fullWidth type="submit"
                        name="buttonRegistration"
                        style={{ padding: ".4em 3em" }}
                        onClick={handleStartRegistration}
                        data-automation-id="button_registration"
                    >
                        {getLabelFromJspVariables('action_next')}
                    </Button>
                }
                {startRegistration && webAuthnRegistration === WebAuthnStatus.SUCCESS &&
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {onSetAsDefault &&
                            <Button variant="contained" onClick={onSetAsDefault}
                                data-automation-id="webauthn_register_set_default_btn">
                                {getLabelFromJspVariables('action_set_passkey_default')}
                            </Button>
                        }
                        <Button variant="outlined" onClick={registerAnother}
                            data-automation-id="webauthn_register_another_btn">
                            {getLabelFromJspVariables('action_register_another')}
                        </Button>
                    </Box>
                }
            </Box>
        </Box>
    );
};

export default PageWebAuthnRegister;
